// src/components/subscription/CheckoutForm.jsx
import React, { useState, useEffect } from 'react';
import {
  ArrowLeft,
  CreditCard,
  Shield,
  Users,
  Calculator,
  AlertCircle,
  Check,
  Loader2,
  Building,
  User,
  Banknote,
  Copy,
  CheckCircle,
  Clock
} from 'lucide-react';
import { PaymentService } from '../../services/paymentService';
import { SubscriptionService } from '../../services/subscriptionService';
import { useAuth } from '../../contexts/AuthContext';

const CheckoutForm = ({ plan, onBack, onSuccess }) => {
  const { currentUser, userProfile } = useAuth();
  const [paymentMethod, setPaymentMethod] = useState('payfast'); // 'payfast' or 'manual'
  const [formData, setFormData] = useState({
    planId: plan.id,
    quantity: plan.type === 'school' ? plan.minUsers || 5 : 1,
    learnerCount: plan.type === 'addon' ? 1 : 0,
    organizationName: userProfile?.schoolName || '',
    billingEmail: userProfile?.email || '',
    agreedToTerms: false,
    // Manual payment fields
    depositName: '',
    depositReference: '',
    depositDate: new Date().toISOString().split('T')[0]
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState('');

  // Bank account details
  const bankDetails = {
    bankName: 'First National Bank (FNB)',
    accountName: 'Mzansi Digital Partners',
    accountNumber: '630 626 49927',
    branchCode: '250655',
    accountType: 'Business Cheque Account',
    swift: 'FIRNZAJJ'
  };

  const calculateTotal = () => {
    const baseAmount = PaymentService.calculateAmount(plan, formData.quantity);
    const addonAmount = plan.type === 'addon' ? plan.price * formData.learnerCount : 0;
    return baseAmount + addonAmount;
  };

  const generatePaymentReference = () => {
    const prefix = plan.type === 'school' ? 'SCH' : plan.type === 'addon' ? 'AI' : 'IND';
    const timestamp = Date.now().toString().slice(-6);
    const userId = currentUser.uid.slice(-4).toUpperCase();
    return `${prefix}${timestamp}${userId}`;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  const copyToClipboard = async (text, field) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(field);
      setTimeout(() => setCopied(''), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const validateForm = () => {
    if (plan.type === 'school' && formData.quantity < (plan.minUsers || 5)) {
      return `Minimum ${plan.minUsers || 5} educators required for school plans`;
    }
    
    if (plan.type === 'addon' && formData.learnerCount < 1) {
      return 'Please specify the number of learners for AI reports';
    }
    
    if (!formData.billingEmail) {
      return 'Billing email is required';
    }

    if (paymentMethod === 'manual') {
      if (!formData.depositName.trim()) {
        return 'Depositor name is required for manual payments';
      }
      if (!formData.depositReference.trim()) {
        return 'Payment reference is required for manual payments';
      }
      if (!formData.depositDate) {
        return 'Deposit date is required for manual payments';
      }
    }
    
    if (!formData.agreedToTerms) {
      return 'Please agree to the terms and conditions';
    }
    
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (paymentMethod === 'payfast') {
        // PayFast payment flow
        const subscriptionData = {
          userId: currentUser.uid,
          planId: plan.id,
          quantity: formData.quantity,
          learnerCount: formData.learnerCount,
          organizationName: formData.organizationName,
          billingEmail: formData.billingEmail,
          paymentMethod: 'payfast'
        };

        const paymentResponse = await PaymentService.createPayment(
          subscriptionData,
          userProfile
        );

        if (paymentResponse.success) {
          PaymentService.processPayment(
            paymentResponse.paymentData,
            paymentResponse.payfastUrl
          );
        } else {
          throw new Error(paymentResponse.error || 'Failed to create payment');
        }
      } else {
        // Manual payment flow
        const subscriptionData = {
          userId: currentUser.uid,
          planId: plan.id,
          planName: plan.name,
          quantity: formData.quantity,
          learnerCount: formData.learnerCount,
          organizationName: formData.organizationName,
          billingEmail: formData.billingEmail,
          totalAmount: calculateTotal(),
          paymentMethod: 'manual',
          paymentDetails: {
            depositName: formData.depositName,
            depositReference: formData.depositReference,
            depositDate: formData.depositDate,
            bankDetails: bankDetails
          },
          status: 'pending_confirmation',
          accessUntil: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), // 7 days from now
          createdAt: new Date(),
          updatedAt: new Date()
        };

        const result = await PaymentService.createManualPayment(subscriptionData);
        
        if (result.success) {
          // Redirect to success page with manual payment confirmation
          onSuccess({
            type: 'manual',
            subscriptionId: result.subscriptionId,
            reference: formData.depositReference,
            accessUntil: subscriptionData.accessUntil
          });
        } else {
          throw new Error(result.error || 'Failed to process manual payment');
        }
      }
        
    } catch (error) {
      console.error('Error processing payment:', error);
      setError(error.message || 'Failed to process payment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const totalAmount = calculateTotal();

  return (
    <div className="max-w-4xl mx-auto space-y-8 mb-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={onBack}
          className="text-[#6e55a3] hover:text-[#6156a6] flex items-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Plans
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Complete Your Subscription</h1>
          <p className="text-gray-600">Choose your preferred payment method</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Form */}
        <div className="lg:col-span-2">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Payment Method Selection */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Payment Method</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* PayFast Option */}
                <label className={`cursor-pointer border-2 rounded-lg p-4 transition-all ${
                  paymentMethod === 'payfast' 
                    ? 'border-[#6e55a3] bg-[#a9a6c4]/10' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}>
                  <input
                    type="radio"
                    name="paymentMethod"
                    value="payfast"
                    checked={paymentMethod === 'payfast'}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                    className="sr-only"
                  />
                  <div className="flex items-center gap-3">
                    <CreditCard className="w-6 h-6 text-[#6e55a3]" />
                    <div>
                      <div className="font-semibold text-gray-900">Instant Payment</div>
                      <div className="text-sm text-gray-600">PayFast secure payment</div>
                      <div className="text-xs text-green-600 font-medium">Instant activation</div>
                    </div>
                  </div>
                </label>

                {/* Manual Payment Option */}
                <label className={`cursor-pointer border-2 rounded-lg p-4 transition-all ${
                  paymentMethod === 'manual' 
                    ? 'border-[#6e55a3] bg-[#a9a6c4]/10' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}>
                  <input
                    type="radio"
                    name="paymentMethod"
                    value="manual"
                    checked={paymentMethod === 'manual'}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                    className="sr-only"
                  />
                  <div className="flex items-center gap-3">
                    <Banknote className="w-6 h-6 text-[#6e55a3]" />
                    <div>
                      <div className="font-semibold text-gray-900">Bank Transfer</div>
                      <div className="text-sm text-gray-600">Direct deposit/EFT</div>
                      <div className="text-xs text-blue-600 font-medium">7-day access pending</div>
                    </div>
                  </div>
                </label>
              </div>
            </div>

            {/* Manual Payment Details */}
            {paymentMethod === 'manual' && (
              <div className="bg-blue-50 rounded-lg border border-blue-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Banknote className="w-5 h-5 text-[#6e55a3]" />
                  Bank Account Details
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-700">Bank Name:</span>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold">{bankDetails.bankName}</span>
                        <button
                          type="button"
                          onClick={() => copyToClipboard(bankDetails.bankName, 'bankName')}
                          className="text-[#6e55a3] hover:text-[#6156a6] p-1"
                        >
                          {copied === 'bankName' ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-700">Account Name:</span>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold">{bankDetails.accountName}</span>
                        <button
                          type="button"
                          onClick={() => copyToClipboard(bankDetails.accountName, 'accountName')}
                          className="text-[#6e55a3] hover:text-[#6156a6] p-1"
                        >
                          {copied === 'accountName' ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-700">Account Number:</span>
                      <div className="flex items-center gap-2">
                        <span className="text-lg font-bold text-[#6e55a3]">{bankDetails.accountNumber}</span>
                        <button
                          type="button"
                          onClick={() => copyToClipboard(bankDetails.accountNumber, 'accountNumber')}
                          className="text-[#6e55a3] hover:text-[#6156a6] p-1"
                        >
                          {copied === 'accountNumber' ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-700">Branch Code:</span>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold">{bankDetails.branchCode}</span>
                        <button
                          type="button"
                          onClick={() => copyToClipboard(bankDetails.branchCode, 'branchCode')}
                          className="text-[#6e55a3] hover:text-[#6156a6] p-1"
                        >
                          {copied === 'branchCode' ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-700">Account Type:</span>
                      <span className="text-sm font-semibold">{bankDetails.accountType}</span>
                    </div>
                    
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-700">Amount to Pay:</span>
                      <span className="text-lg font-bold text-green-600">R{totalAmount.toFixed(2)}</span>
                    </div>
                  </div>
                </div>

                {/* Payment Reference */}
                <div className="bg-white rounded-lg p-4 mb-4">
                  <div className="flex justify-between items-center">
                    <div>
                      <span className="text-sm font-medium text-gray-700">Payment Reference:</span>
                      <div className="text-lg font-bold text-[#6e55a3]">{generatePaymentReference()}</div>
                      <div className="text-xs text-gray-500">Use this as your payment reference</div>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        const reference = generatePaymentReference();
                        copyToClipboard(reference, 'reference');
                        handleInputChange('depositReference', reference);
                      }}
                      className="bg-[#6e55a3] text-white px-3 py-2 rounded-md hover:bg-[#6156a6] flex items-center gap-2"
                    >
                      {copied === 'reference' ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      Copy
                    </button>
                  </div>
                </div>

                {/* Manual Payment Form Fields */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Depositor Name *
                    </label>
                    <input
                      type="text"
                      value={formData.depositName}
                      onChange={(e) => handleInputChange('depositName', e.target.value)}
                      placeholder="Name on bank account"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#6e55a3]"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Payment Reference *
                    </label>
                    <input
                      type="text"
                      value={formData.depositReference}
                      onChange={(e) => handleInputChange('depositReference', e.target.value)}
                      placeholder="Payment reference"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#6e55a3]"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Deposit Date *
                    </label>
                    <input
                      type="date"
                      value={formData.depositDate}
                      onChange={(e) => handleInputChange('depositDate', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#6e55a3]"
                      required
                    />
                  </div>
                </div>

                {/* 7-Day Access Notice */}
                <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center gap-2 text-green-800">
                    <Clock className="w-5 h-5" />
                    <span className="font-medium">7-Day Access Period</span>
                  </div>
                  <p className="text-sm text-green-700 mt-1">
                    You'll get immediate access to all features for 7 days while we verify your payment. 
                    Full subscription activates once payment is confirmed.
                  </p>
                </div>
              </div>
            )}

            {/* Organization Details */}
            {plan.type === 'school' && (
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Building className="w-5 h-5 text-[#6e55a3]" />
                  Organization Details
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      School/Organization Name *
                    </label>
                    <input
                      type="text"
                      value={formData.organizationName}
                      onChange={(e) => handleInputChange('organizationName', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#6e55a3]"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Number of Educators *
                    </label>
                    <input
                      type="number"
                      min={plan.minUsers || 5}
                      value={formData.quantity}
                      onChange={(e) => handleInputChange('quantity', parseInt(e.target.value) || 0)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#6e55a3]"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Minimum {plan.minUsers || 5} educators required
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* AI Reports Configuration */}
            {plan.type === 'addon' && (
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Users className="w-5 h-5 text-[#6e55a3]" />
                  AI Reports Configuration
                </h3>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Number of Learners *
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={formData.learnerCount}
                    onChange={(e) => handleInputChange('learnerCount', parseInt(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#6e55a3]"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    AI reports will be available for up to {formData.learnerCount} learners across all subjects
                  </p>
                </div>
              </div>
            )}

            {/* Billing Information */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <User className="w-5 h-5 text-[#6e55a3]" />
                Billing Information
              </h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Billing Email *
                </label>
                <input
                  type="email"
                  value={formData.billingEmail}
                  onChange={(e) => handleInputChange('billingEmail', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#6e55a3]"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Invoices and subscription updates will be sent to this email
                </p>
              </div>
            </div>

            {/* Terms & Conditions */}
            <div className="bg-gray-50 rounded-lg p-6">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.agreedToTerms}
                  onChange={(e) => handleInputChange('agreedToTerms', e.target.checked)}
                  className="mt-1 rounded border-gray-300 text-[#6e55a3] focus:ring-[#6e55a3]"
                  required
                />
                <div className="text-sm text-gray-700">
                  <p>
                    I agree to the{' '}
                    <a href="/terms" target="_blank" className="text-[#6e55a3] hover:underline">
                      Terms of Service
                    </a>
                    {' '}and{' '}
                    <a href="/privacy" target="_blank" className="text-[#6e55a3] hover:underline">
                      Privacy Policy
                    </a>
                    . I understand that my subscription will automatically renew at the end of each billing period.
                    {paymentMethod === 'manual' && (
                      <span className="block mt-1 font-medium text-blue-700">
                        For manual payments, I will receive 7 days access pending payment verification.
                      </span>
                    )}
                  </p>
                </div>
              </label>
            </div>

            {/* Error Display */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            {/* Submit Button */}
            <div className="flex justify-end mb-6">
              <button
                type="submit"
                disabled={loading}
                className="bg-[#6e55a3] text-white px-8 py-3 rounded-lg font-semibold hover:bg-[#6156a6] disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Processing...
                  </>
                ) : paymentMethod === 'payfast' ? (
                  <>
                    <CreditCard className="w-4 h-4" />
                    Proceed to Payment
                  </>
                ) : (
                  <>
                    <Banknote className="w-4 h-4" />
                    Submit Manual Payment
                  </>
                )}
              </button>
            </div>
          </form>
        </div>

        {/* Order Summary */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg border border-gray-200 p-6 sticky top-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Order Summary</h3>
            
            <div className="space-y-4">
              {/* Plan Details */}
              <div className="flex justify-between items-start">
                <div>
                  <p className="font-medium text-gray-900">{plan.name}</p>
                  <p className="text-sm text-gray-600">{plan.description}</p>
                  {plan.type === 'school' && (
                    <p className="text-sm text-gray-500">{formData.quantity} educators</p>
                  )}
                  {plan.type === 'addon' && (
                    <p className="text-sm text-gray-500">{formData.learnerCount} learners</p>
                  )}
                </div>
                <div className="text-right">
                  {plan.discountedPrice ? (
                    <div>
                      <p className="text-sm text-gray-500 line-through">R{plan.price}</p>
                      <p className="font-semibold text-[#6e55a3]">R{plan.discountedPrice}</p>
                    </div>
                  ) : (
                    <p className="font-semibold">R{plan.price}</p>
                  )}
                </div>
              </div>

              {/* Early Bird Savings */}
              {plan.discountedPrice && (
                <div className="flex justify-between items-center text-green-600">
                  <span className="text-sm">Early Bird Savings</span>
                  <span className="font-semibold">-R{plan.originalPrice - plan.discountedPrice}</span>
                </div>
              )}

              <hr className="border-gray-200" />

              {/* Total */}
              <div className="flex justify-between items-center">
                <span className="text-lg font-semibold text-gray-900">Total</span>
                <span className="text-2xl font-bold text-[#6e55a3]">R{totalAmount.toFixed(2)}</span>
              </div>

              <p className="text-xs text-gray-500">
                Billed {plan.duration === 'annual' ? 'annually' : 'per term'}
              </p>
            </div>

            {/* Security Badge */}
            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Shield className="w-4 h-4 text-green-500" />
                <span>
                  {paymentMethod === 'payfast' ? 'Secure payment by PayFast' : 'Secure bank transfer'}
                </span>
              </div>
              {paymentMethod === 'manual' && (
                <div className="flex items-center gap-2 text-sm text-gray-600 mt-1">
                  <Clock className="w-4 h-4 text-blue-500" />
                  <span>7-day access while verifying payment</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutForm;